import React, { useCallback, useEffect, useState, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

/** DRIFT OVERVIEW — slate-level triage view of every project sorted by
 *  median ΔE drift desc. Reads only the cached drift_score; never runs a
 *  stats pass on load. Click a row to jump into Grade Room for that
 *  project. "Measure" button on un-measured rows fires the analysis-only
 *  preview pass. */
export default function DriftOverviewPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/grade-room/drift-overview`, authHeaders());
      startTransition(() => setRows(data.projects || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load drift overview");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inflight = React.useRef(false);

  const measure = async (projectId) => {
    if (inflight.current) return;
    inflight.current = true;
    setBusy(projectId);
    try {
      const { data } = await axios.post(
        `${API}/api/projects/${projectId}/grades/auto-balance/preview`,
        { target: "median" }, authHeaders());
      if (data.clips_analyzed) {
        toast.success(`Drift measured · ${data.clips_analyzed} clip(s)`);
        await load();
      } else {
        toast.error("No hero proxies cached — open the project in Grade Room first");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Measure failed");
    }
    inflight.current = false;
    setBusy("");
  };

  const chipColor = (de) => {
    if (de === null || de === undefined) return "bg-white/10 text-white/40";
    if (de > 8) return "bg-red-500/25 text-red-300";
    if (de > 4) return "bg-amber-500/25 text-amber-300";
    if (de > 2) return "bg-cyan-500/25 text-cyan-300";
    return "bg-emerald-500/25 text-emerald-300";
  };

  const measured = rows.filter((r) => r.drift.status === "measured");
  const unmeasured = rows.filter((r) => r.drift.status !== "measured");

  return (
    <div className="min-h-screen bg-[#0c0a09] text-white/90 px-6 py-6"
      data-testid="drift-overview-page">
      <header className="flex items-center gap-4 pb-5 border-b border-white/10 mb-6">
        <button onClick={() => navigate("/recipes")}
          className="text-white/40 hover:text-orange-300 text-sm"
          data-testid="drift-back-btn">← Back</button>
        <div>
          <p className="font-mono text-[10px] tracking-[0.2em] text-orange-400">SLATE TRIAGE</p>
          <h1 className="text-xl font-extrabold tracking-tight">DRIFT OVERVIEW</h1>
        </div>
        <span className="ml-auto font-mono text-[10px] text-white/30 tracking-widest">
          {measured.length} measured · {unmeasured.length} not measured
        </span>
      </header>

      {measured.length > 0 && (
        <section className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">
            Measured · sorted by median ΔE desc (worst-drift first)
          </p>
          <div className="space-y-2">
            {measured.map((p) => (
              <div key={p.id}
                data-testid={`drift-row-${p.id}`}
                className="flex items-center gap-4 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-white/20">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {p.name}
                    {p.brand_tag && <span className="text-white/40 ml-2">· {p.brand_tag}</span>}
                  </p>
                  <p className="text-[10px] font-mono text-white/40 mt-0.5">
                    {p.clips_done} clip(s) done · target {p.drift.target_mode} ·
                    measured {new Date(p.drift.measured_at).toLocaleString()}
                  </p>
                </div>
                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded ${chipColor(p.drift.median_de)}`}
                  data-testid={`drift-chip-${p.id}`}>
                  median ΔE {p.drift.median_de}
                </span>
                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded ${chipColor(p.drift.max_de)}`}>
                  max ΔE {p.drift.max_de}
                </span>
                <button onClick={() => navigate(`/grade-room?project=${p.id}`)}
                  data-testid={`drift-grade-${p.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-orange-500 text-black hover:bg-orange-400">
                  Grade
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {unmeasured.length > 0 && (
        <section>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 mb-3">
            Not measured · click Measure to run the (analysis-only) lαβ pass
          </p>
          <div className="space-y-2">
            {unmeasured.map((p) => (
              <div key={p.id}
                data-testid={`drift-row-${p.id}`}
                className="flex items-center gap-4 px-4 py-3 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/70 truncate">
                    {p.name}
                    {p.brand_tag && <span className="text-white/30 ml-2">· {p.brand_tag}</span>}
                  </p>
                  <p className="text-[10px] font-mono text-white/30 mt-0.5">
                    {p.clips_done} clip(s) done
                    {p.drift.stale && " · cache stale (new clip or render since last measure)"}
                  </p>
                </div>
                {p.clips_done > 0 && (
                  <button onClick={() => measure(p.id)}
                    disabled={busy === p.id}
                    data-testid={`drift-measure-${p.id}`}
                    className="px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-wider border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-30">
                    {busy === p.id ? "Measuring…" : "Measure"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
